import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import { useLocation } from 'react-router-dom';
import { VISITOR_STATS } from './siteConfig';

// Satu "window" online = 5 menit. Pengunjung yang aktif di window sekarang
// atau window sebelumnya dihitung sedang online (±10 menit terakhir).
const ONLINE_WINDOW_MS = 5 * 60 * 1000;
const HEARTBEAT_MS = 60 * 1000;
const LAST_VISIT_KEY = 'vs:lastVisitDate';

const numberFormat = new Intl.NumberFormat('id-ID');

function isCountableBrowser() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  if (navigator.webdriver) return false;
  const ua = navigator.userAgent || '';
  return !/ReactSnap|HeadlessChrome|Lighthouse|bot|crawl|spider/i.test(ua);
}

// Kunci tanggal mengikuti WIB (Asia/Jakarta), format YYYY-MM-DD.
function jakartaDateKey(offsetDays = 0) {
  const date = new Date(Date.now() + offsetDays * 24 * 60 * 60 * 1000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function readStorage(area, key) {
  try {
    return window[area].getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(area, key, value) {
  try {
    window[area].setItem(key, value);
  } catch {
    // Mode privat / storage penuh: abaikan, statistik tetap tampil.
  }
}

function counterNamespace() {
  const base = VISITOR_STATS.namespace;
  return process.env.NODE_ENV === 'production' ? base : `${base}-dev`;
}

async function counterRequest(action, key) {
  const url = `${VISITOR_STATS.endpoint}/${action}/${counterNamespace()}/${key}`;
  const response = await fetch(url, { cache: 'no-store' });
  if (response.status === 404) return 0;
  if (!response.ok) {
    throw new Error(`Visitor counter ${action} ${key} failed: ${response.status}`);
  }
  const data = await response.json();
  const value = Number(data?.value ?? data?.count);
  return Number.isFinite(value) ? value : 0;
}

const hitCounter = (key) => counterRequest('hit', key);
const getCounter = (key) => counterRequest('get', key);

async function syncVisits(today) {
  const alreadyCountedToday = readStorage('localStorage', LAST_VISIT_KEY) === today;
  const request = alreadyCountedToday ? getCounter : hitCounter;
  const [todayCount, totalCount] = await Promise.all([
    request(`visits-${today}`),
    request('visits-total'),
  ]);
  if (!alreadyCountedToday) writeStorage('localStorage', LAST_VISIT_KEY, today);
  return { today: todayCount, total: totalCount };
}

async function syncOnline() {
  const bucket = Math.floor(Date.now() / ONLINE_WINDOW_MS);
  const flagKey = `vs:online:${bucket}`;
  const alreadyInBucket = readStorage('sessionStorage', flagKey) === '1';
  const [current, previous] = await Promise.all([
    alreadyInBucket ? getCounter(`online-${bucket}`) : hitCounter(`online-${bucket}`),
    getCounter(`online-${bucket - 1}`),
  ]);
  if (!alreadyInBucket) writeStorage('sessionStorage', flagKey, '1');
  return Math.max(1, current + previous);
}

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// Animasi angka naik (ease-out) setiap kali target berubah.
function useCountUp(target, duration = 1100) {
  const [value, setValue] = useState(0);
  const currentRef = useRef(0);

  useEffect(() => {
    if (target === null || target === undefined) return undefined;
    const from = currentRef.current;
    const delta = target - from;
    if (delta === 0) return undefined;
    if (prefersReducedMotion()) {
      currentRef.current = target;
      setValue(target);
      return undefined;
    }

    let frame;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = Math.round(from + delta * eased);
      currentRef.current = next;
      setValue(next);
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);

  return value;
}

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.9; }
  70% { transform: scale(2.4); opacity: 0; }
  100% { transform: scale(2.4); opacity: 0; }
`;

function LiveDot() {
  return (
    <Box
      component='span'
      aria-hidden
      sx={{
        position: 'relative',
        display: 'inline-block',
        width: 9,
        height: 9,
        borderRadius: '50%',
        backgroundColor: '#22c55e',
        boxShadow: '0 0 0 2px rgba(34,197,94,0.25)',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          backgroundColor: '#22c55e',
          animation: `${pulse} 1.8s ease-out infinite`,
        },
      }}
    />
  );
}

function Odometer({ value, minDigits = 6 }) {
  const digits = String(Math.max(0, value)).padStart(minDigits, '0').split('');
  return (
    <Box
      sx={{ display: 'flex', gap: 0.6, mt: 1.5 }}
      aria-label={`Total kunjungan ${numberFormat.format(value)}`}
    >
      {digits.map((digit, index) => (
        <Box
          key={`${index}-${digits.length}`}
          sx={{
            position: 'relative',
            width: { xs: 30, sm: 34 },
            height: { xs: 42, sm: 48 },
            borderRadius: '8px',
            background:
              'linear-gradient(180deg, #3b3f44 0%, #1d1f22 52%, #26292d 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow:
              'inset 0 1px 0 rgba(255,255,255,0.08), 0 6px 14px rgba(0,0,0,0.35)',
            color: '#fff',
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontWeight: 800,
            fontSize: { xs: '1.35rem', sm: '1.55rem' },
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              height: 1,
              backgroundColor: 'rgba(0,0,0,0.55)',
            },
          }}
        >
          {digit}
        </Box>
      ))}
    </Box>
  );
}

function StatTile({ icon: Icon, label, value, accent }) {
  const animated = useCountUp(typeof value === 'number' ? value : 0);
  const display = typeof value === 'number' ? numberFormat.format(animated) : '—';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.06)',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 38,
          height: 38,
          borderRadius: '10px',
          display: 'grid',
          placeItems: 'center',
          color: accent,
          backgroundColor: `${accent}1f`,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: '#fff',
            fontWeight: 900,
            fontSize: { xs: '1.2rem', sm: '1.3rem' },
            lineHeight: 1.1,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {display}
        </Typography>
        <Typography
          sx={{
            color: '#9aa0a6',
            fontSize: '0.72rem',
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function VisitorStats() {
  const { pathname } = useLocation();
  const [stats, setStats] = useState({
    today: null,
    yesterday: null,
    total: null,
    hitsToday: null,
    online: null,
  });
  const [failed, setFailed] = useState(false);
  const animatedTotal = useCountUp(
    stats.total === null ? null : stats.total + (VISITOR_STATS.baseTotal || 0),
    1500,
  );

  // Hit = setiap halaman dibuka (termasuk pindah halaman di SPA).
  useEffect(() => {
    if (!VISITOR_STATS.enabled || !isCountableBrowser()) return undefined;
    let cancelled = false;
    hitCounter(`hits-${jakartaDateKey()}`)
      .then((hitsToday) => {
        if (!cancelled) setStats((prev) => ({ ...prev, hitsToday }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  // Visit = pengunjung unik per hari (sekali per browser per hari) + total.
  useEffect(() => {
    if (!VISITOR_STATS.enabled || !isCountableBrowser()) return undefined;
    let cancelled = false;
    const today = jakartaDateKey();

    syncVisits(today)
      .then(({ today: todayCount, total }) => {
        if (!cancelled) setStats((prev) => ({ ...prev, today: todayCount, total }));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    getCounter(`visits-${jakartaDateKey(-1)}`)
      .then((yesterday) => {
        if (!cancelled) setStats((prev) => ({ ...prev, yesterday }));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  // Online = heartbeat tiap menit selama tab terlihat.
  useEffect(() => {
    if (!VISITOR_STATS.enabled || !isCountableBrowser()) return undefined;
    let cancelled = false;

    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      syncOnline()
        .then((online) => {
          if (!cancelled) setStats((prev) => ({ ...prev, online }));
        })
        .catch(() => {});
    };

    tick();
    const interval = window.setInterval(tick, HEARTBEAT_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, []);

  if (!VISITOR_STATS.enabled || failed) return null;

  return (
    <Box
      component='section'
      aria-label='Statistik pengunjung'
      sx={{
        maxWidth: 1100,
        mx: 'auto',
        mt: { xs: 4, md: 5 },
        p: { xs: 2.5, md: 3 },
        borderRadius: '16px',
        background:
          'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr)' },
        gap: { xs: 3, md: 4 },
        alignItems: 'center',
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <LiveDot />
          <Typography
            sx={{
              color: '#FF6A00',
              fontWeight: 900,
              fontSize: '0.78rem',
              letterSpacing: 1.2,
              textTransform: 'uppercase',
            }}
          >
            Statistik Pengunjung
          </Typography>
        </Box>
        <Odometer value={animatedTotal} />
        <Typography sx={{ mt: 1, color: '#9aa0a6', fontSize: '0.8rem' }}>
          Total kunjungan ke bydbsdserpong.com
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 1.5,
        }}
      >
        <StatTile
          icon={TodayRoundedIcon}
          label='Visit hari ini'
          value={stats.today}
          accent='#4ade80'
        />
        <StatTile
          icon={HistoryRoundedIcon}
          label='Visit kemarin'
          value={stats.yesterday}
          accent='#60a5fa'
        />
        <StatTile
          icon={VisibilityRoundedIcon}
          label='Hits hari ini'
          value={stats.hitsToday}
          accent='#fbbf24'
        />
        <StatTile
          icon={PeopleAltRoundedIcon}
          label='Sedang online'
          value={stats.online}
          accent='#f472b6'
        />
      </Box>
    </Box>
  );
}

export default VisitorStats;
